import { Building2 } from 'lucide-react';
import { useWorkspace } from '@/context/WorkspaceContext.js';
import { cn } from '@/lib/utils.js';

const variants = {
  sidebar:
    'flex items-center gap-2 rounded-[10px] border border-ha-sidebar-border bg-white/[0.04] px-3 py-2.5',
  page: 'flex max-w-md items-center gap-2 rounded-card border border-ha-border2 bg-ha-card px-3 py-2.5',
} as const;

type Props = { variant?: keyof typeof variants };

export function WorkspaceSwitcher({ variant = 'sidebar' }: Props) {
  const { empresas, activeEmpresa, activeEmpresaId, setActiveEmpresaId, isLoading } = useWorkspace();
  const shellClass = variants[variant];
  const isPage = variant === 'page';
  const textClass = isPage ? 'text-ha-text' : 'text-white';
  const mutedClass = isPage ? 'text-ha-muted' : 'text-white/50';

  if (isLoading) {
    return <div className={cn(shellClass, 'text-xs', mutedClass)}>Carregando empresa…</div>;
  }

  const single = empresas.length <= 1;
  const label = activeEmpresa?.name ?? 'Selecionar empresa';

  if (single) {
    return (
      <div className={shellClass} title={label} aria-label="Empresa ativa">
        <Building2 className="h-4 w-4 shrink-0 text-accent" strokeWidth={1.5} />
        <span className={cn('min-w-0 flex-1 truncate text-sm font-medium', textClass)}>{label}</span>
      </div>
    );
  }

  return (
    <label className={cn(shellClass, 'cursor-pointer')}>
      <Building2 className="h-4 w-4 shrink-0 text-accent" strokeWidth={1.5} />
      <select
        className={cn(
          'min-w-0 flex-1 cursor-pointer truncate border-0 bg-transparent text-sm font-medium outline-none',
          textClass,
        )}
        value={activeEmpresaId ?? ''}
        onChange={(e) => setActiveEmpresaId(e.target.value || null)}
        aria-label="Empresa ativa"
      >
        {empresas.map((e) => (
          <option key={e.id} value={e.id} className="bg-ha-card text-ha-text">
            {e.name}
          </option>
        ))}
      </select>
    </label>
  );
}
